
import React, { useEffect } from "react";
import FullCalendar from "@fullcalendar/react";
import dayGridPlugin from "@fullcalendar/daygrid";
import timeGridPlugin from "@fullcalendar/timegrid";
import interactionPlugin from "@fullcalendar/interaction";
import { useIsMobile } from "@/hooks/use-mobile";
import { Lock, Calendar, Car, FileText, User, MapPin } from "lucide-react";

interface ServiceCalendarProps {
  events: any[];
  handleDateClick: (info: any) => void;
  handleEventClick: (info: any) => void;
  activeView: string;
  onViewMount: (viewInfo: any) => void;
  isManagementView?: boolean;
  calendarConfig: {
    toolbarConfig: any;
    businessHours: any[];
    timeRange: { minTime: string; maxTime: string };
    calendarViews: any;
    calendarHeight: string;
    initialView?: string;
  };
}

export const ServiceCalendar = ({
  events,
  handleDateClick,
  handleEventClick,
  activeView,
  onViewMount,
  isManagementView = false,
  calendarConfig
}: ServiceCalendarProps) => {
  const { toolbarConfig, businessHours, timeRange, calendarViews, calendarHeight, initialView } = calendarConfig;
  const isMobile = useIsMobile();
  
  const calendarRef = React.useRef<FullCalendar | null>(null);
  
  useEffect(() => {
    if (calendarRef.current && activeView) {
      const calendarApi = calendarRef.current.getApi();
      if (calendarApi.view.type !== activeView) {
        calendarApi.changeView(activeView);
      }
    }
  }, [activeView]);

  const customButtons = {
    prev: {
      text: '←',
      click: function() {
        if (calendarRef.current) {
          calendarRef.current.getApi().prev();
        }
      }
    },
    next: {
      text: '→',
      click: function() {
        if (calendarRef.current) {
          calendarRef.current.getApi().next();
        }
      }
    }
  };

  const getResponsiveHeight = () => {
    if (!isMobile) return 'auto';
    
    if (activeView === 'dayGridMonth') return '280px';
    if (activeView === 'timeGridDay') return '550px';
    return '340px';
  };

  const calendarStyles = `
    .fc-event {
      padding: 2px 4px;
      font-weight: 500;
      background-color: white !important;
      border-width: 0;
      box-shadow: 0 1px 2px rgba(0, 0, 0, 0.05);
      transition: all 0.2s ease;
    }
    
    /* Base styles for events by status */
    .event-new {
      border-left: 8px solid #FBBF24 !important;
    }
    .event-accepted, .event-confirmed {
      border-left: 8px solid #10B981 !important;
    }
    .event-rejected, .event-cancelled {
      border-left: 8px solid #EF4444 !important;
    }
    .event-blocked {
      border-left: 8px solid #7E69AB !important;
    }
    .event-completed {
      border-left: 8px solid #8B5CF6 !important;
      border-right: 2px solid #8B5CF6 !important;
    }
    .event-travel {
      border-left: 8px solid #9CA3AF !important;
    }
    
    /* More subtle hover effects for events */
    .fc-event:hover {
      box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1);
      z-index: 5;
    }
    
    /* Ensure events are clickable */
    .fc-event {
      pointer-events: auto !important;
      cursor: pointer !important;
      position: relative;
      z-index: 2;
    }
    
    .fc-event-main {
      pointer-events: auto !important;
      z-index: 3;
    }
    
    /* Special styling for other customers' bookings - GRAY (all statuses) */
    .event-other-booking {
      border-left: 8px solid #6B7280 !important;
      border-right: 2px solid #6B7280 !important;
      background-color: rgba(243, 244, 246, 0.1) !important;
      font-weight: 500 !important;
    }
    
    /* Enhanced hover effect for other customers' bookings */
    .event-other-booking:hover {
      box-shadow: 0 2px 4px rgba(0, 0, 0, 0.15) !important;
      transform: translateY(-1px);
      background-color: rgba(243, 244, 246, 0.2) !important;
      z-index: 10;
    }

    /* Special styling for user's own bookings - NEW (Blue) */
    .event-user-booking-new {
      border-left: 8px solid #3B82F6 !important;
      border-right: 2px solid #3B82F6 !important;
      background-color: rgba(219, 234, 254, 0.1) !important;
      font-weight: 600 !important;
    }
    
    /* Enhanced hover effect for new user bookings */
    .event-user-booking-new:hover {
      box-shadow: 0 2px 4px rgba(0, 0, 0, 0.15) !important;
      transform: translateY(-1px);
      background-color: rgba(219, 234, 254, 0.2) !important;
      z-index: 10;
    }

    /* Special styling for user's own bookings - ACCEPTED (Green) */
    .event-user-booking-accepted {
      border-left: 8px solid #10B981 !important;
      border-right: 2px solid #10B981 !important;
      background-color: rgba(209, 250, 229, 0.1) !important;
      font-weight: 600 !important;
    }
    
    /* Enhanced hover effect for accepted user bookings */
    .event-user-booking-accepted:hover {
      box-shadow: 0 2px 4px rgba(0, 0, 0, 0.15) !important;
      transform: translateY(-1px);
      background-color: rgba(209, 250, 229, 0.2) !important;
      z-index: 10;
    }

    /* Special styling for user's own bookings - COMPLETED (Purple) */
    .event-user-booking-completed {
      border-left: 8px solid #8B5CF6 !important;
      border-right: 2px solid #8B5CF6 !important;
      background-color: rgba(237, 233, 254, 0.1) !important;
      font-weight: 600 !important;
    }
    
    /* Enhanced hover effect for completed user bookings */
    .event-user-booking-completed:hover {
      box-shadow: 0 2px 4px rgba(0, 0, 0, 0.15) !important;
      transform: translateY(-1px);
      background-color: rgba(237, 233, 254, 0.2) !important;
      z-index: 10;
    }
    
    .fc-daygrid-event {
      padding: 2px 4px !important;
      margin-bottom: 1px !important;
    }
    
    .fc-daygrid-event-harness {
      margin-bottom: 1px !important;
    }
    
    .fc-event-main {
      padding: 1px 2px !important;
    }
    
    .fc-toolbar-title {
      font-size: ${isMobile ? '1rem' : '1.25rem'} !important;
      font-weight: 600;
    }
    .fc-event-title {
      font-size: 0.875rem !important;
      font-weight: 600 !important;
      white-space: nowrap;
      overflow: hidden;
      text-overflow: ellipsis;
      padding: 0 4px;
      color: #333333 !important;
    }
    .fc-daygrid-event {
      border-radius: 4px;
      margin-bottom: 2px;
    }
    .fc-timegrid-event {
      border-radius: 4px;
    }
    .fc-col-header-cell {
      font-weight: 600;
      text-transform: uppercase;
      font-size: ${isMobile ? '0.7rem' : '0.8rem'};
    }
    .fc-event-time {
      display: none;
    }
    
    .fc-timegrid-slot {
      height: ${isMobile ? '100px' : '40px'} !important;
    }
    
    .fc-timegrid-slot-label {
      font-weight: ${isMobile ? '600' : '500'};
      color: #666;
    }
    
    .fc-prev-button, .fc-next-button {
      background-color: #f3f4f6 !important;
      border: 1px solid #e5e7eb !important;
      font-size: ${isMobile ? '1.2rem' : '1rem'} !important;
      padding: ${isMobile ? '5px 10px' : '4px 8px'} !important;
      margin: 0 3px !important;
      color: #374151 !important;
    }
    
    .fc-prev-button:hover, .fc-next-button:hover {
      background-color: #e5e7eb !important;
    }
    
    .fc-button {
      padding: ${isMobile ? '8px 12px' : '6px 10px'} !important;
    }
  `;

  const getSlotDuration = () => {
    if (calendarViews && calendarViews.timeGrid && calendarViews.timeGrid.slotDuration) {
      return calendarViews.timeGrid.slotDuration;
    }
    return '00:30:00'; // Default to 30 minutes if not specified
  };

  const renderEventContent = (eventInfo: any) => {
    const booking = eventInfo.event.extendedProps?.booking;
    const isUserBooking = eventInfo.event.extendedProps?.isUserBooking;
    const currentView = calendarRef.current?.getApi().view.type;
    
    // Debug event rendering
    if (eventInfo.event.startStr.includes('08:00') || eventInfo.event.startStr.includes('11:00')) {
      console.log("🎨 Rendering event:", {
        id: eventInfo.event.id,
        start: eventInfo.event.startStr,
        hasBooking: !!booking,
        bookingId: booking?.id,
        bookingStatus: booking?.status,
        extendedProps: eventInfo.event.extendedProps
      });
    }
    
    if (booking?.status === 'blocked') {
      return (
        <div className="flex items-center px-1 py-0.5 text-xs">
          <Lock className="w-3 h-3 mr-1" />
          <span>Blocked</span>
        </div>
      );
    } 
    
    if (booking?.status === 'travel') {
      return (
        <div className="flex items-center px-1 py-0.5 text-xs">
          <Car className="w-3 h-3 mr-1" />
          <span>Travel</span>
        </div>
      );
    }
    
    if ((currentView === 'dayGridMonth' || currentView === 'timeGridWeek') && isManagementView) {
      return (
        <div className="flex flex-col px-1 py-0.5 text-xs">
          <div className="flex justify-between items-center w-full">
            <span className={`font-medium truncate max-w-20`}>
              {booking?.customer_name || "Booking"}
            </span>
          </div>
          {booking?.note && (
            <div className="flex items-center mt-0.5">
              <FileText className="w-2.5 h-2.5" />
            </div>
          )}
        </div>
      );
    }
    
    if (currentView === 'dayGridMonth' || currentView === 'timeGridWeek') {
      return (
        <div className="flex items-center px-1 py-0.5 text-xs">
          {isUserBooking ? (
            <User className="w-3 h-3 mr-1 text-green-600" />
          ) : (
            <Calendar className="w-3 h-3 mr-1" />
          )}
          <span className={`truncate ${isUserBooking ? 'text-green-700' : ''}`}>
            {isUserBooking ? "My Booking" : "Booked"}
          </span>
        </div>
      );
    }
    
    if (isManagementView) {
      return (
        <div className="flex flex-col px-1 py-0.5">
          <div className="flex justify-between items-center w-full text-xs">
            <span className="font-medium">{eventInfo.timeText}</span>
            {booking?.status && (
              <span className={`text-xs ${
                booking.status === 'new' ? "text-amber-600 font-medium" : 
                booking.status === 'accepted' ? "text-green-600 font-medium" :
                booking.status === 'cancelled' ? "text-red-600 font-medium" : 
                "text-gray-600"
              }`}>
                {booking.status.charAt(0).toUpperCase() + booking.status.slice(1)}
              </span>
            )}
          </div>
          
          {booking?.customer_name && (
            <div className="flex items-center mt-1 text-xs">
              <User className="w-3 h-3 mr-1" />
              <span className="font-medium truncate">{booking.customer_name}</span>
            </div>
          )}
          
          {booking?.customer_address && currentView === 'timeGridDay' && (
            <div className="flex items-center mt-1 text-xs text-gray-600">
              <MapPin className="w-3 h-3 mr-1" />
              <span className="truncate">{booking.customer_address}</span>
            </div>
          )}
          
          {booking?.note && (
            <div className="flex items-center mt-1 text-xs text-gray-600">
              <FileText className="w-3 h-3 mr-1" />
              <span className="truncate">{booking.note}</span>
            </div>
          )}
        </div>
      );
    }
    
    return (
      <div className="flex flex-col px-1 py-0.5">
        <div className="flex justify-between items-center w-full text-xs">
          <span className="font-medium">{eventInfo.timeText}</span>
          {isUserBooking && (
            <span className="text-green-600 font-medium">My Booking</span>
          )}
        </div>
        
        {isUserBooking && booking?.note && (
          <div className="flex items-center mt-1 text-xs text-gray-600">
            <FileText className="w-3 h-3 mr-1" />
            <span className="truncate">{booking.note}</span>
          </div>
        )}
      </div>
    );
  };

  return (
    <div className={`calendar-container ${isMobile ? 'text-sm' : 'text-base'} ${isManagementView ? 'management-view' : ''}`}>
      <style>{calendarStyles}</style>
      <div className="calendar-wrapper" style={{ fontSize: isMobile ? '0.75rem' : '0.85rem' }}>
        <FullCalendar
          ref={calendarRef}
          plugins={[dayGridPlugin, timeGridPlugin, interactionPlugin]}
          initialView={initialView || activeView}
          timeZone="local"
          headerToolbar={{
            left: 'prev',
            center: 'title',
            right: 'next'
          }}
          customButtons={customButtons}
          editable={false}
          selectable={true}
          selectMirror={true}
          dayMaxEvents={isMobile ? 1 : true}
          events={events}
          eventClick={handleEventClick}
          dateClick={handleDateClick}
          height={getResponsiveHeight()}
          allDaySlot={false}
          slotDuration={getSlotDuration()}
          slotLabelInterval={isMobile ? "01:00" : "01:00"}
          slotMinTime={timeRange.minTime}
          slotMaxTime={timeRange.maxTime}
          businessHours={businessHours}
          views={calendarViews}
          viewDidMount={onViewMount}
          stickyHeaderDates={true}
          weekends={true}  // Explicitly enable weekends
          eventTimeFormat={{
            hour: 'numeric',
            minute: '2-digit',
            meridiem: 'short'
          }}
          eventDisplay="block"
          slotLabelFormat={{
            hour: 'numeric',
            minute: '2-digit',
            meridiem: 'short'
          }}
          nowIndicator={true}
          eventContent={renderEventContent}
          eventClassNames={(arg) => {
            const classes = ["rounded-md"];
            
            // Add cursor-pointer to all events with booking data
            if (arg.event.extendedProps?.booking) {
              classes.push("cursor-pointer");
            }
            
            // Vendors see colored bars for all bookings to their service
            if (isManagementView) {
              const booking = arg.event.extendedProps.booking;
              if (booking) {
                if (booking.status === 'blocked') {
                  // Blocked slots get gray bars for vendors
                  classes.push("event-other-booking");
                } else if (booking.status === 'completed') {
                  classes.push("event-user-booking-completed");
                } else if (booking.status === 'accepted' || booking.status === 'confirmed') {
                  classes.push("event-user-booking-accepted");
                } else {
                  classes.push("event-user-booking-new");
                }
              }
            } else {
              // Customers see colored bars only for their own bookings
              if (arg.event.extendedProps?.isUserBooking) {
                const booking = arg.event.extendedProps.booking;
                if (booking?.status === 'completed') {
                  classes.push("event-user-booking-completed");
                } else if (booking?.status === 'accepted' || booking?.status === 'confirmed') {
                  classes.push("event-user-booking-accepted");
                } else {
                  classes.push("event-user-booking-new");
                }
              } else if (arg.event.extendedProps?.booking && !arg.event.extendedProps?.isUserBooking) {
                // Other customers' bookings - gray bars for customer view
                classes.push("event-other-booking");
              }
            }
            
            if (arg.event.extendedProps?.booking?.status === 'travel') {
              classes.push("opacity-80");
            }
            
            return classes;
          }}
        />
      </div>
    </div>
  );
};
